// Copyright 2024 - MinIO, Inc. All rights reserved.

import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import { catRpc } from "../../../api";
import { setErrorSnackBarMessage, setSnackBarMessage } from "../../systemSlice";
import { getBytes, getBytesNumber } from "../../../common/utils";
import { loadGlobalSitesAsync } from "../../systemThunks";
import { MinioEnvVars, MinioNodeSelector } from "../../../api/observeRpcApi";
import {
  MinioImagePullSecrets,
  MinioImageRegistryConfig,
  MinioResourceRequirements,
} from "../../../api/catalogApi";

export const addCatalogAsync = createAsyncThunk(
  "catalog/addCatalogAsync",
  async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState() as RootState;

    const selectedSite = state.addCatalog.selectedSite;
    const name = state.addCatalog.name;
    const namespace = state.addCatalog.namespace;
    const volumeSize = state.addCatalog.volumeSize;
    const storageClass = state.addCatalog.storageClass;
    const minioURL = state.addCatalog.minioURL;
    const accessKey = state.addCatalog.accessKey;
    const secretKey = state.addCatalog.secretKey;
    const sizeFactor = state.addCatalog.sizeFactor;
    const buckets = state.addCatalog.buckets;
    const envVars = state.addCatalog.envVars;
    const nodeSelector = state.addCatalog.nodeSelectorPairs;
    const tolerations = state.addCatalog.tolerations;
    const image = state.addCatalog.image;
    const imagePullPolicy = state.addCatalog.imagePullPolicy;
    const imagePullSecrets = state.addCatalog.imagePullSecrets;
    const registry = state.addCatalog.imageRegistry;
    const registryUsername = state.addCatalog.imageRegistryUsername;
    const registryPassword = state.addCatalog.imageRegistryPassword;
    const resourceCPURequest = state.addCatalog.resourcesCPURequest;
    const resourceMemoryRequest = state.addCatalog.resourcesMemoryRequest;
    const resourceCPULimit = state.addCatalog.resourcesCPULimit;
    const resourceMemoryLimit = state.addCatalog.resourcesMemoryLimit;
    const currentSize = getBytesNumber(volumeSize, sizeFactor, true);
    let localEnvVars: MinioEnvVars = {
      vars: envVars,
    };
    let localImagePullSecrets: MinioImagePullSecrets = {
      secrets: imagePullSecrets,
    };
    let localImageRegistryConfig: MinioImageRegistryConfig | undefined;
    if (registry !== "" || registryUsername !== "" || registryPassword !== "") {
      localImageRegistryConfig = {
        registry: registry,
        username: registryUsername,
        password: registryPassword,
      };
    }
    let localImageName = image?.trim() !== "" ? image : undefined;

    let localResourceRequirements: MinioResourceRequirements | undefined;
    if (
      resourceCPURequest ||
      resourceMemoryRequest ||
      resourceCPULimit ||
      resourceMemoryLimit
    ) {
      localResourceRequirements = {};
    }
    if (localResourceRequirements) {
      if (resourceCPURequest?.trim() !== "") {
        localResourceRequirements.requests = {
          cpu: resourceCPURequest,
        };
      }
      if (resourceCPULimit?.trim() !== "") {
        localResourceRequirements.limits = {
          cpu: resourceCPULimit,
        };
      }
      if (resourceMemoryRequest?.trim() !== "") {
        if (localResourceRequirements.requests && resourceMemoryRequest) {
          localResourceRequirements.requests.memory = getBytes(
            resourceMemoryRequest,
            "Gi",
            true,
          );
        }
      }
      if (resourceMemoryLimit !== "") {
        if (localResourceRequirements.limits && resourceMemoryLimit) {
          localResourceRequirements.limits.memory = getBytes(
            resourceMemoryLimit,
            "Gi",
            true,
          );
        }
      }
    }

    let localNodeSelector: MinioNodeSelector = {
      keyValueMap: nodeSelector.reduce(
        (acc, pair) => {
          if (pair.key !== "") {
            acc[pair.key] = pair.value;
          }
          return acc;
        },
        {} as { [key: string]: string },
      ),
    };

    return catRpc.api
      .addCatalog(selectedSite, {
        name,
        namespace,
        volumeSize: `${currentSize}`,
        storageClass,
        minioUrl: minioURL,
        accessKey: accessKey,
        secretKey: secretKey,
        buckets,
        env: localEnvVars,
        tolerations: tolerations,
        nodeSelector: localNodeSelector,
        image: localImageName,
        imagePullPolicy: imagePullPolicy,
        imagePullSecrets: localImagePullSecrets,
        imageRegistryConfig: localImageRegistryConfig,
        resources: localResourceRequirements,
      })
      .then((res) => {
        dispatch(setSnackBarMessage("Added Catalog Instance successfully"));
        dispatch(loadGlobalSitesAsync());
        return res.data;
      })
      .catch((err) => {
        console.error(err);
        dispatch(setErrorSnackBarMessage(err.error));
        return rejectWithValue(err.error);
      });
  },
);
